import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Dialog } from 'radix-ui'
import { ArrowRight, BookOpen, Loader2, Scale, Send, Sparkles, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import {
  askAssistant, getAssistantStatus, getAssistantSuggestions, rateAssistant, ASSISTANT_OPEN_EVENT,
  type AssistantAnswer, type AssistantCard,
} from '@/api/assistant'
import { useVisibleNav } from '@/components/layout/Sidebar'
import { useAuth } from '@/hooks/useAuth'

/**
 * In-app assistant (design brief: Intercom Fin / Workday Assistant / Linear "Ask" pattern):
 * a floating launcher above Back-to-top, a right slide-over (full screen on phones, safe-area
 * aware in the Android app), role-aware quick chips, contextual help for the current page,
 * answers with cited sources, deep-link buttons, a legal disclaimer, and thumbs feedback.
 *
 * Every deep link is checked twice: the server only offers routes the role may open, and the
 * client keeps only routes present in this user's visible nav (roles + switched-on modules).
 */
interface Turn { q: string; a?: AssistantAnswer; error?: string; rated?: 1 | -1 }

const ALWAYS_OK = ['/ess/profile', '/notifications', '/dashboard']

export function AssistantLauncher() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const sections = useVisibleNav()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [turns, setTurns] = useState<Turn[]>([])
  const endRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const reachable = useMemo(() => new Set([...sections.flatMap((s) => s.items.map((i) => i.to)), ...ALWAYS_OK]), [sections])
  const allowed = (to: string | null | undefined): to is string => !!to && reachable.has(to.split('?')[0])

  const status = useQuery({ queryKey: ['assistant', 'status'], queryFn: getAssistantStatus, enabled: !!user, staleTime: 300_000, retry: false, meta: { inlineError: true } })
  const sugg = useQuery({
    queryKey: ['assistant', 'suggestions', pathname], queryFn: () => getAssistantSuggestions(pathname),
    enabled: open && !!user, staleTime: 60_000, retry: false, meta: { inlineError: true },
  })

  const ask = useMutation({
    mutationFn: (question: string) => askAssistant(question, pathname),
    meta: { silentError: true },
    onSuccess: (a, question) => setTurns((t) => t.map((x) => (x.q === question && !x.a && !x.error ? { ...x, a } : x))),
    onError: (err: unknown, question) => {
      const msg = (err as { response?: { status?: number; data?: { error?: { message?: string } } } })?.response
      const text = msg?.data?.error?.message ?? (msg?.status === 429 ? 'You are asking very quickly. Try again in a minute.' : 'The assistant could not answer right now. Please try again.')
      setTurns((t) => t.map((x) => (x.q === question && !x.a && !x.error ? { ...x, error: text } : x)))
    },
  })

  const submit = (text?: string) => {
    const question = (text ?? q).trim()
    if (question.length < 2 || ask.isPending) return
    setTurns((t) => [...t, { q: question }])
    setQ('')
    ask.mutate(question)
  }

  useEffect(() => {
    const onOpen = (e: Event) => {
      setOpen(true)
      const question = (e as CustomEvent<{ question?: string }>).detail?.question
      if (question) window.setTimeout(() => submit(question), 50)
    }
    window.addEventListener(ASSISTANT_OPEN_EVENT, onOpen)
    return () => window.removeEventListener(ASSISTANT_OPEN_EVENT, onOpen)
  })
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }) }, [turns])

  if (!user || status.data?.enabled === false) return null

  const go = (to: string) => { setOpen(false); navigate(to) }
  const rate = (i: number, r: 1 | -1) => {
    const t = turns[i]
    if (!t.a || t.rated) return
    setTurns((all) => all.map((x, j) => (j === i ? { ...x, rated: r } : x)))
    rateAssistant(t.a.message_id, r).catch(() => setTurns((all) => all.map((x, j) => (j === i ? { ...x, rated: undefined } : x))))
  }
  const chips = (sugg.data?.chips ?? []).filter((c) => !c.route || allowed(c.route))
  const context = (sugg.data?.context ?? []).filter((c) => allowed(c.route))

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          aria-label="Open assistant"
          data-testid="assistant-launcher"
          className="fixed right-5 z-40 inline-flex h-11 items-center gap-2 rounded-full bg-primary pl-3.5 pr-4 text-sm font-medium text-primary-foreground shadow-e3 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 print:hidden"
          style={{ bottom: 'calc(var(--tabbar-h, 0px) + var(--sab, env(safe-area-inset-bottom, 0px)) + 4.25rem)' }}
        >
          <Sparkles className="size-4" aria-hidden />
          <span className="hidden sm:inline">Ask</span>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-overlay/20 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus() }}
          data-testid="assistant-panel"
          className="fixed inset-y-0 right-0 z-[70] flex w-full flex-col border-l border-border bg-card shadow-e4 sm:w-[420px] data-[state=open]:animate-in data-[state=open]:slide-in-from-right"
          style={{ paddingTop: 'var(--sat, env(safe-area-inset-top, 0px))', paddingBottom: 'var(--sab, env(safe-area-inset-bottom, 0px))' }}
        >
          <header className="flex items-center gap-2.5 border-b border-border px-4 py-3">
            <span className="grid size-8 place-items-center rounded-lg bg-primary/10 text-primary"><Sparkles className="size-4" /></span>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-sm font-semibold text-foreground">Assistant</Dialog.Title>
              <p className="truncate text-xs text-muted-foreground">
                How-tos, Indian payroll & labour-law FAQs, quick navigation{status.data?.llm_available ? ' · AI on' : ''}
              </p>
            </div>
            <Dialog.Close className="grid size-9 place-items-center rounded-md text-muted-foreground hover:bg-surface-hover" aria-label="Close assistant"><X className="size-4" /></Dialog.Close>
          </header>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
            {turns.length === 0 && (
              <>
                {context.length > 0 && (
                  <section>
                    <h3 className="mb-2 text-xs font-medium text-muted-foreground">Help for this page</h3>
                    <div className="space-y-2">{context.map((c) => <CardBlock key={c.id} c={c} onGo={go} allowed={allowed} />)}</div>
                  </section>
                )}
                <section>
                  <h3 className="mb-2 text-xs font-medium text-muted-foreground">Try asking</h3>
                  <div className="flex flex-wrap gap-2">
                    {chips.map((c) => (
                      <button key={c.id} type="button" onClick={() => submit(c.label)}
                        className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-foreground hover:bg-surface-hover">
                        {c.label}
                      </button>
                    ))}
                    {sugg.isLoading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                  </div>
                </section>
              </>
            )}
            {turns.map((t, i) => (
              <div key={i} className="space-y-2">
                <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground">{t.q}</div>
                {!t.a && !t.error && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Thinking…</div>}
                {t.error && <div role="alert" className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">{t.error}</div>}
                {t.a && (
                  <div className="space-y-2 rounded-2xl rounded-bl-sm border border-border bg-background px-3 py-2.5" data-testid="assistant-answer">
                    {t.a.notice && <p className="text-xs text-warning">{t.a.notice}</p>}
                    <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">{t.a.answer}</p>
                    {t.a.actions.filter((a) => allowed(a.to)).length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {t.a.actions.filter((a) => allowed(a.to)).map((a) => (
                          <button key={a.to} type="button" onClick={() => go(a.to)}
                            className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/15">
                            {a.label} <ArrowRight className="size-3" />
                          </button>
                        ))}
                      </div>
                    )}
                    {t.a.sources.length > 0 && (
                      <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                        <BookOpen className="mt-0.5 size-3 shrink-0" /> Source: {t.a.sources.slice(0, 2).map((s) => s.source).join('; ')}
                      </p>
                    )}
                    {t.a.disclaimer && (
                      <p className="flex items-start gap-1.5 rounded-md bg-warning/10 px-2 py-1.5 text-[11px] text-foreground" data-testid="assistant-disclaimer">
                        <Scale className="mt-0.5 size-3 shrink-0" /> {t.a.disclaimer}
                      </p>
                    )}
                    <div className="flex items-center gap-1 pt-0.5">
                      <span className="mr-1 text-[11px] text-muted-foreground">{t.a.tier === 'llm' ? 'AI answer' : 'From help'} · Helpful?</span>
                      <button type="button" aria-label="Helpful" aria-pressed={t.rated === 1} onClick={() => rate(i, 1)} disabled={!!t.rated}
                        className={`grid size-7 place-items-center rounded-md hover:bg-surface-hover ${t.rated === 1 ? 'text-success' : 'text-muted-foreground'}`}><ThumbsUp className="size-3.5" /></button>
                      <button type="button" aria-label="Not helpful" aria-pressed={t.rated === -1} onClick={() => rate(i, -1)} disabled={!!t.rated}
                        className={`grid size-7 place-items-center rounded-md hover:bg-surface-hover ${t.rated === -1 ? 'text-danger' : 'text-muted-foreground'}`}><ThumbsDown className="size-3.5" /></button>
                    </div>
                    {t.a.related.filter((c) => c.kind === 'legal' || allowed(c.route)).slice(0, 2).map((c) => <CardBlock key={c.id} c={c} onGo={go} allowed={allowed} compact />)}
                  </div>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <form className="flex items-center gap-2 border-t border-border p-3" onSubmit={(e) => { e.preventDefault(); submit() }}>
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              maxLength={1000}
              placeholder="Ask about leave, payroll, PF, ESI…"
              aria-label="Ask the assistant"
              className="h-10 min-w-0 flex-1 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-content-placeholder focus:ring-2 focus:ring-ring"
            />
            <button type="submit" aria-label="Send" disabled={q.trim().length < 2 || ask.isPending}
              className="grid size-10 place-items-center rounded-md bg-primary text-primary-foreground disabled:opacity-50">
              {ask.isPending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
            </button>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function CardBlock({ c, onGo, allowed, compact }: { c: AssistantCard; onGo: (to: string) => void; allowed: (to: string | null | undefined) => boolean; compact?: boolean }) {
  return (
    <div className={`rounded-lg border border-border bg-card ${compact ? 'px-2.5 py-2' : 'px-3 py-2.5'}`}>
      <p className="text-xs font-semibold text-foreground">{c.title}</p>
      {!compact && <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{c.answer}</p>}
      {c.route && allowed(c.route) && (
        <button type="button" onClick={() => onGo(c.route as string)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-link hover:underline">
          Open <ArrowRight className="size-3" />
        </button>
      )}
    </div>
  )
}
